import { Dispatch, FC, SetStateAction, useState } from "react";
import { Dropdown, Menu, Space, Table, Tag, Typography, Switch, message, Modal } from "antd";
import type { ColumnsType } from "antd/es/table";
import { MasterBoxData, GetAllMasterBoxesRequest } from "services/interface";
import { EllipsisOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import ModalEdit from "./ModalEdit";
import { useUpdateMasterBox } from "hooks/useMasterData";
import { UpdateMasterBoxRequest } from "services/interface";
//import ModalEditDeliveryPoint from "./ModalEditDeliveryPoint";
interface TableSettingBoxProps {
  data: MasterBoxData[] | undefined;
  onDeleteConfigs: (id: number) => void; // ✅ แก้ไข: เปลี่ยนจาก string เป็น number เพื่อรองรับ SERIAL PRIMARY KEY
  limit: number;
  offset: number;
  setLimit: Dispatch<SetStateAction<number>>;
  setOffset: Dispatch<SetStateAction<number>>;
  total: string | undefined;
  searchParams: GetAllMasterBoxesRequest;
  setSearchParams: Dispatch<SetStateAction<GetAllMasterBoxesRequest>>;
  onSearch: () => void;
  refetch: () => void;
}

const testStatus = (status: boolean) => {
  if (status) {
    return "เปิด";
  } else {
    return "ปิด";
  }
};

const colorStatus = (status: boolean) => {
  if (status) {
    return "green";
  } else {
    return "red";
  }
};

const TableSettingBox: FC<TableSettingBoxProps> = ({
  data,
  onDeleteConfigs,
  limit,
  offset,
  setLimit,
  setOffset,
  total,
  searchParams,
  setSearchParams,
  onSearch,
  refetch,
}) => {
  const [editData, setEditData] = useState<MasterBoxData | undefined>();
  const [isMoadlEdit, setIsModalEdit] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [deleteItem, setDeleteItem] = useState<MasterBoxData | null>(null);
  
  const { mutate: updateMasterBox } = useUpdateMasterBox();


  const handleDeleteClick = (record: MasterBoxData) => {
    setDeleteItem(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteItem) {
      onDeleteConfigs(deleteItem.id);
      setDeleteModalVisible(false);
      setDeleteItem(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteItem(null);
  };

  const handleToggleStatus = (record: MasterBoxData) => {
    const requestData: UpdateMasterBoxRequest = {
      id: record.id,
      name: record.name,
      description: record.description,
      width: record.width,
      height: record.height,
      length: record.length,
      weight: record.weight,
      is_active: !record.is_active,
    };

    updateMasterBox(requestData, {
      onSuccess: (data) => {
        console.log('🔄 [TableSettingBox] Status update success:', data);
        
        // Check multiple possible success indicators
        const isSuccess = data.success === true || 
                         (data as any).status === 'success' || 
                         (data as any).status === 200 ||
                         (!(data as any).error_message && data.data);
        
        if (isSuccess) {
          refetch();
          const statusText = !record.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน';
          message.success(`เปลี่ยนสถานะเป็น "${statusText}" สำเร็จ`);
        } else {
          message.error("เปลี่ยนสถานะไม่สำเร็จ");
        }
      },
      onError: (error) => {
        console.error('Error updating status:', error);
        message.error(`เปลี่ยนสถานะไม่สำเร็จ: ${error.message}`);
      }
    });
  };

  const columns: ColumnsType<MasterBoxData> = [
    {
      title: "ลำดับ",
      dataIndex: "index",
      align: "center",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 50,
    },
    {
      title: "ชื่อ",
      dataIndex: "name",
      width: 200,
      render: (_text, record) => {
        return (
          <Typography.Link
            onClick={() => {
              setEditData(record);
              setIsModalEdit(true);
            }}
            style={{
              cursor: "pointer",
              fontWeight: "400",
              textDecoration: "underline",
            }}
          >
            {record.name}
          </Typography.Link>
        );
      },
    },
    {
      title: "กว้าง",
      dataIndex: "width",
      width: 100,
      align: "center",
      render: (_text, record) => {
        return `${record.width} ${record.unit}`;
      },
    },
    {
      title: "ยาว",
      dataIndex: "length",
      width: 100,
      align: "center",
      render: (_text, record) => {
        return `${record.length} ${record.unit}`;
      },
    },
    {
      title: "สูง",
      dataIndex: "height",
      width: 100,
      align: "center",
      render: (_text, record) => {
        return `${record.height} ${record.unit}`;
      },
    },
    {
      title: "สถานะ",
      dataIndex: "is_active",
      width: 100,
      align: "center",
      render: (_text, record) => {
        return (
          <Tag color={colorStatus(record.is_active)}>
            {testStatus(record.is_active)}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      key: "id",
      fixed: "right",
      width: 100,
      render: (_text, record) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <Space size="middle">
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item key="0">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>สถานะ:</span>
                      <Switch
                        size="small"
                        checked={record.is_active}
                        onChange={() => handleToggleStatus(record)}
                        checkedChildren="เปิด"
                        unCheckedChildren="ปิด"
                      />
                    </div>
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    key="1"
                    onClick={() => {
                      setEditData(record);
                      setIsModalEdit(true);
                    }}
                    icon={<EditOutlined />}
                  >
                    แก้ไข
                  </Menu.Item>
                  <Menu.Item
                    key="2"
                    onClick={() => {
                      handleDeleteClick(record);
                    }}
                    icon={<DeleteOutlined />}
                  >
                    ลบ
                  </Menu.Item>
                </Menu>
              }
            >
              <div>
                <EllipsisOutlined />
              </div>
            </Dropdown>
          </Space>
        </div>
      ),
    },
  ];


  return (
    <div>
      <Table
        scroll={{ x: true }}
        size="middle"
        columns={columns}
        dataSource={data}
        pagination={{
          pageSize: limit,
          total: parseInt(total || "0"),
          current: Math.floor(offset / limit) + 1,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} จาก ${total} รายการ`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => {
            const newLimit = pageSize || 10;
            const newOffset = (page - 1) * newLimit;
            setLimit(newLimit);
            setOffset(newOffset);
            
            // Update search params with new pagination
            setSearchParams(prev => ({
              ...prev,
              page_number: page,
              row_per_page: newLimit
            }));
            
            // Trigger search with new pagination
            onSearch();
          },
        }}
      />
      <ModalEdit
        editData={editData}
        isShow={isMoadlEdit}
        onClose={() => {
          setIsModalEdit(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        title="ยืนยันการลบ"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="ลบ"
        cancelText="ยกเลิก"
        okButtonProps={{ danger: true }}
      >
        <p>คุณต้องการลบกล่อง "{deleteItem?.name}" หรือไม่?</p>
        <p style={{ color: '#999', fontSize: '14px' }}>
          การดำเนินการนี้ไม่สามารถยกเลิกได้
        </p>
      </Modal>
    </div>
  );
};

export default TableSettingBox;
